import React, { useEffect, useState } from 'react';
import { Button, Popover, Select, Divider, Space, Input, Checkbox, Form } from 'antd';
import { MenuOutlined, LinkOutlined, PlusOutlined } from '@ant-design/icons';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import arrayMove from 'array-move';
import { Field, GridLayout, GridLayoutColumn } from '../../models';
import { apiClient, apiErrorMessage } from '../../utils';

export function GridLayoutsForm(props: {
  fields?: Field[];
  defaultGridLayout?: GridLayout;
  gridLayouts?: GridLayout[];
  activeColumns?: GridLayoutColumn[];
  setActiveColumns?: (columns: GridLayoutColumn[]) => void;
  onGridLayoutSaved?: () => void;
}) {
  const [gridId, setGridId] = useState<string>();
  const [filteredActiveColumns, setFilteredActiveColumns] = useState<GridLayoutColumn[]>();
  const [displayFilteredActiveColumns, setDisplayFilteredActiveColumns] = useState<GridLayoutColumn[]>();
  const [userFilter, setUserFilter] = useState<string>();
  const [inactiveFields, setInactiveFields] = useState<Field[]>();
  const [displayInactiveFields, setDisplayInactiveFields] = useState<Field[]>();
  const [selectedGridLayoutId, setSelectedGridLayoutId] = useState<string>();
  const [showAddNewPopover, setShowAddNewPopover] = useState(false);
  const [showSaveAsPopover, setShowSaveAsPopover] = useState(false);

  useEffect(() => {
    if (!props.activeColumns) {
      return;
    }

    const filteredColumns = props.activeColumns.filter(ac => props.fields?.some(f => f.id === ac.fieldId));
    setFilteredActiveColumns(filteredColumns);

    if (userFilter) {
      const displayFilteredColumns = filteredColumns.filter(c => {
        const field = props.fields?.find(f => f.id === c.fieldId);
        return (field?.displayName || field?.name)?.toLowerCase().includes(userFilter.toLowerCase());
      });
      setDisplayFilteredActiveColumns(displayFilteredColumns);
    } else {
      setDisplayFilteredActiveColumns(filteredColumns);
    }

    const actives: Field[] = [];
    for (const activeColumn of filteredColumns) {
      const field = props.fields?.find(f => f.id === activeColumn.fieldId);
      if (!field) {
        continue;
      }
      actives.push(field);
    }
    const inactives = props.fields?.filter(f => !actives.some(a => a.id === f.id)).sort((f1, f2) => (f1.name < f2.name ? -1 : 1));
    setInactiveFields(inactives);

    if (userFilter) {
      const displayInactives = inactives?.filter(f => f?.name.toLowerCase().includes(userFilter.toLowerCase()));
      setDisplayInactiveFields(displayInactives);
    } else {
      setDisplayInactiveFields(inactives);
    }
  }, [props.activeColumns, props.fields, userFilter]);

  useEffect(() => {
    if (!props.fields?.length) {
      return;
    }
    setGridId(props.fields[0].gridId);
  }, [props.fields]);

  useEffect(() => {
    const selected = props.gridLayouts?.find(gv => gv.isSelected);
    setSelectedGridLayoutId(selected?.id || props.defaultGridLayout?.id);
  }, [props.defaultGridLayout?.id, props.gridLayouts]);

  const onSearchChange = (e: any) => {
    const value = e.target.value;
    if (!value || value.toString().trim() === '') {
      setUserFilter(undefined);
    }
    setUserFilter(value);
  };

  const setActiveColumns = (newActiveColumns: GridLayoutColumn[]) => {
    if (!props.setActiveColumns) {
      return;
    }

    const identifierColumns = newActiveColumns
      .filter(c => {
        const field = props.fields?.find(f => f.id === c.fieldId);
        if (field?.isIdentifier) {
          return c;
        }
        return null;
      })
      .filter(c => !!c);

    const fixedColumns = newActiveColumns.filter(c => c.isFixed && !identifierColumns.some(ic => ic.fieldId === c.fieldId));
    const restColumns = newActiveColumns.filter(
      c => !identifierColumns.some(ic => ic.fieldId === c.fieldId) && !fixedColumns.some(fc => fc.fieldId === c.fieldId),
    );

    const sortedData = [...identifierColumns, ...fixedColumns, ...restColumns];
    props.setActiveColumns(sortedData);
  };

  const onChangeIsFixedValue = (fieldId: string) => {
    if (!filteredActiveColumns?.length) {
      return;
    }

    const newActiveColumns = [...filteredActiveColumns];
    newActiveColumns?.forEach((c: GridLayoutColumn) => {
      if (c.fieldId === fieldId) {
        c.isFixed = !c.isFixed;
      }
    });
    setActiveColumns(newActiveColumns);
  };

  const onSortEnd = ({ oldIndex, newIndex }: any) => {
    if (oldIndex === newIndex || !filteredActiveColumns?.length) {
      return;
    }

    const newActiveColumns = arrayMove(filteredActiveColumns, oldIndex, newIndex).filter(el => !!el);
    setActiveColumns(newActiveColumns);
  };

  const onInactiveSelectChange = (e: any, field: Field) => {
    if (!e.target.checked || !filteredActiveColumns) {
      return;
    }
    const newColumn: GridLayoutColumn = {
      fieldId: field.id,
      isFixed: false,
      width: field.name.length * 10 + 40,
    };
    setActiveColumns([...filteredActiveColumns, newColumn]);
  };

  const onActiveSelectChange = (e: any, column: GridLayoutColumn) => {
    if (e.target.checked || !filteredActiveColumns) {
      return;
    }
    setActiveColumns(filteredActiveColumns.filter(c => c.fieldId !== column.fieldId));

    const field = props.fields?.find(f => f.id === column.fieldId);
    if (!field) {
      return;
    }

    if (!inactiveFields) {
      setInactiveFields([field]);
    } else {
      let inactives = [...inactiveFields, field];
      inactives = inactives.sort((f1, f2) => (f1.name < f2.name ? -1 : 1));
      setInactiveFields(inactives);
    }
  };

  const onSelectAllClick = () => {
    if (!inactiveFields?.length || !filteredActiveColumns) {
      return;
    }
    const newActiveColumns = inactiveFields.map(f => {
      const newColumn: GridLayoutColumn = {
        fieldId: f.id,
        isFixed: false,
        width: f.name.length * 10 + 40,
      };
      return newColumn;
    });
    setActiveColumns([...filteredActiveColumns, ...newActiveColumns]);
  };

  const onDeselectAllClick = () => {
    if (!props.fields?.length) {
      return;
    }
    const newActiveColumns = props.fields
      .filter(f => f.isIdentifier)
      .map(f => {
        const newColumn: GridLayoutColumn = {
          fieldId: f.id,
          isFixed: false,
          width: f.name.length * 10 + 40,
        };
        return newColumn;
      });
    setActiveColumns([...newActiveColumns]);
  };

  const onAddNewVisibleChange = (visible: boolean) => {
    setShowAddNewPopover(visible);
  };

  const onAddNewCancel = () => {
    setShowAddNewPopover(false);
  };

  const onSaveAsVisibleChange = (visible: boolean) => {
    setShowSaveAsPopover(visible);
  };

  const onSaveAsCancel = () => {
    setShowSaveAsPopover(false);
  };

  const onResetClicked = () => {
    if (!props.defaultGridLayout) {
      return;
    }
    setActiveColumns(props.gridLayouts?.find(f => f.id === selectedGridLayoutId)?.columns || props.defaultGridLayout?.columns);
  };

  const onSaveClicked = () => {
    apiClient
      .put(`grid-layouts/${selectedGridLayoutId}`, { columns: filteredActiveColumns, isSelected: true })
      .then(() => {
        if (props.onGridLayoutSaved) {
          props.onGridLayoutSaved();
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  };

  const onDeleteClick = () => {
    apiClient
      .delete(`grid-layouts/${selectedGridLayoutId}`)
      .then(() => {
        if (props.setActiveColumns && props.defaultGridLayout) {
          props.setActiveColumns(props.defaultGridLayout?.columns);
        }
        if (props.onGridLayoutSaved) {
          props.onGridLayoutSaved();
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  };

  const onChangeSelectedGridLayout = async (id: string) => {
    const currentSelectedGridLayoutId = selectedGridLayoutId;
    setSelectedGridLayoutId(id);
    const gridLayout = id === 'default' ? props.defaultGridLayout : props.gridLayouts?.find(v => v.id === id);

    if (!gridLayout) {
      return;
    }

    if (props.setActiveColumns) {
      props.setActiveColumns(gridLayout.columns);
    }

    if (id !== 'default') {
      await apiClient
        .patch(`grid-layouts/${id}/selected`)
        .then(() => {})
        .catch(() => {});
    } else {
      await apiClient
        .patch(`grid-layouts/${currentSelectedGridLayoutId}/unselected`)
        .then(() => {})
        .catch(() => {});
    }
  };

  const SortableItem = SortableElement(({ column }: any) => ColumnRow(column));
  const DragHandle = SortableHandle(() => <MenuOutlined style={{ cursor: 'grab', color: '#999', float: 'right' }} />);
  const SortableList = SortableContainer(({ columns }: any) => {
    return (
      <div>
        <table style={{ width: '100%' }}>
          <tbody>
            {(columns?.length || 0) > 0 &&
              columns?.map((column: GridLayoutColumn, i: number) => <SortableItem key={`field-${i}`} index={i} column={column} />)}
          </tbody>
        </table>
      </div>
    );
  });

  const content = () => {
    return (
      <>
        <Input allowClear onChange={onSearchChange} style={{ marginBottom: '1rem' }} />
        <div style={{ maxHeight: '60vh', overflow: 'auto', marginBottom: '1rem' }}>
          <SortableList
            columns={displayFilteredActiveColumns}
            useDragHandle
            disableAutoscroll
            helperClass="row-dragging"
            onSortEnd={onSortEnd}
          />
          {(displayFilteredActiveColumns?.length || 0) > 0 && (displayInactiveFields?.length || 0) > 0 && <Divider />}
          <div>
            <table>
              <tbody>
                {(displayInactiveFields?.length || 0) > 0 &&
                  displayInactiveFields?.map((field: Field) => (
                    <tr style={{ height: '30px' }} key={field.name}>
                      <td style={{ width: '28px' }}>
                        <Checkbox
                          onChange={e => {
                            onInactiveSelectChange(e, field);
                          }}
                        />
                      </td>
                      <td className="name-colum">{field.name}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
        <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #eee' }}>
          <Space>
            <Button size="small" onClick={onSelectAllClick}>
              Tümünü seç
            </Button>
            <Button size="small" onClick={onDeselectAllClick}>
              Tüm seçimleri kaldır
            </Button>
          </Space>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <Popover
              trigger="click"
              visible={showAddNewPopover}
              onVisibleChange={onAddNewVisibleChange}
              destroyTooltipOnHide={true}
              content={
                <CreateGridLayoutForm
                  gridId={gridId}
                  columns={props.defaultGridLayout?.columns}
                  onSaved={props.onGridLayoutSaved}
                  onCancel={onAddNewCancel}
                />
              }
            >
              <Button size="small" icon={<PlusOutlined />}>
                Yeni
              </Button>
            </Popover>
          </div>
          <div>
            <Space>
              <Button size="small" onClick={onResetClicked}>
                Sıfırla
              </Button>

              <Popover
                trigger="click"
                visible={showSaveAsPopover}
                onVisibleChange={onSaveAsVisibleChange}
                destroyTooltipOnHide={true}
                content={
                  <CreateGridLayoutForm
                    gridId={gridId}
                    columns={filteredActiveColumns}
                    onSaved={props.onGridLayoutSaved}
                    onCancel={onSaveAsCancel}
                  />
                }
              >
                <Button size="small">Farklı Kaydet</Button>
              </Popover>
              <Button size="small" onClick={onSaveClicked} disabled={selectedGridLayoutId === 'default'}>
                Kaydet
              </Button>
              <Button size="small" onClick={onDeleteClick} disabled={selectedGridLayoutId === 'default'}>
                Sil
              </Button>
            </Space>
          </div>
        </div>
      </>
    );
  };

  const ColumnRow = (column: GridLayoutColumn) => {
    const field = props.fields?.find(f => f.id === column.fieldId);

    return (
      <tr style={{ height: '30px' }}>
        <td style={{ width: '28px' }}>
          <Checkbox
            defaultChecked={true}
            onChange={e => {
              onActiveSelectChange(e, column);
            }}
            disabled={field?.isIdentifier}
          />
        </td>
        <td style={{ width: '28px', padding: '3px', cursor: 'pointer' }}>
          {!field?.isIdentifier && (
            <LinkOutlined
              onClick={() => onChangeIsFixedValue(column.fieldId)}
              style={{ opacity: field?.isIdentifier || column.isFixed ? '1' : '0.3' }}
              disabled={field?.isIdentifier}
            />
          )}
        </td>
        <td className="name-colum">{field?.displayName || field?.name}</td>
        <td style={{ width: '28px', textAlign: 'right' }}>{column.isFixed ? null : <DragHandle />}</td>
      </tr>
    );
  };

  const title = () => {
    return (
      <Select
        onChange={onChangeSelectedGridLayout}
        value={selectedGridLayoutId}
        style={{ marginTop: '0.5rem', marginBottom: '0.5rem', width: '100%' }}
      >
        {props.defaultGridLayout && <Select.Option value={props.defaultGridLayout.id}>{props.defaultGridLayout.name}</Select.Option>};
        {(props.gridLayouts?.length || 0) > 0 &&
          props.gridLayouts?.map(g => {
            return <Select.Option value={g.id}>{g.name}</Select.Option>;
          })}
      </Select>
    );
  };

  return (
    <>
      <Popover placement="bottom" title={title} content={content} trigger="click" overlayStyle={{ width: '400px' }}>
        <Button>
          <MenuOutlined /> Veri elemanı
        </Button>
      </Popover>
    </>
  );
}

function CreateGridLayoutForm(props: { gridId?: string; columns?: GridLayoutColumn[]; onSaved?: () => void; onCancel?: () => void }) {
  const [loading, setLoading] = useState(false);

  const onFinish = (values: any) => {
    setLoading(true);
    apiClient
      .post(`grid-layouts`, { ...values, gridId: props.gridId, isSelected: true, columns: props.columns })
      .then(() => {
        if (props.onSaved) {
          props.onSaved();
        }
        if (props.onCancel) {
          props.onCancel();
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Form layout="vertical" name="basic" onFinish={onFinish}>
      <Form.Item label="İsim" name="name" rules={[{ required: true, message: 'Zorunlu alan' }]}>
        <Input />
      </Form.Item>
      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            Gönder
          </Button>
          <Button type="default" htmlType="button" onClick={props.onCancel}>
            İptal
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}
